import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { PiSummaryDto } from "@hisense/shared";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Panel } from "@/components/ui/Panel";
import { useProformaInvoices } from "@/api/hooks";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { canOpenReference, referenceLabel } from "@/lib/salesReference";
export function poWriteError(err: unknown, t: TFunction, fallbackKey = "purchaseOrders.errorSave"): string {
  if (err instanceof ApiClientError) {
    if (err.code === "pi_already_linked") return t("purchaseOrders.piAlreadyLinked", {number:err.fields?.number ?? "—",po:err.fields?.po ?? "—"});
    const key = `poWorkspace.${err.code}`;
    if (t(key) !== key) return t(key);
  }
  return getLocalizedError(err,t,fallbackKey);
}
export interface PoPiLinkPanelProps {
  isNew: boolean; linkedPiId: string | null; linkedPiNo: string | null;
  onPick: (pi: PiSummaryDto) => void; onClear: () => void; disabled?: boolean;
  forBuild?: boolean;
  /** Không tự bọc thẻ — dùng khi khối nằm trong một nhóm của trang sửa đơn mua. */
  bare?: boolean;
}
function Frame({ bare, children }: { bare?: boolean; children: React.ReactNode }) {
  return bare ? <div>{children}</div> : <Panel className="mt-3 p-4">{children}</Panel>;
}
export function PoPiLinkPanel(props: PoPiLinkPanelProps) {
  const {t}=useTranslation();
  if (!props.isNew) return <Frame bare={props.bare}><FieldLabel>{t("purchaseOrders.linkedPi")}</FieldLabel>
    {/* YC-01 (07/10/2026): PI của khách người xem không được thấy hiện "Đơn nội bộ", không liên kết. */}
    {props.linkedPiId && canOpenReference(props.linkedPiNo) ? <Link className="font-mono text-sm text-brand" to={`/proforma-invoices/${props.linkedPiId}`}>{props.linkedPiNo}</Link> : props.linkedPiId ? <p className="font-mono text-sm" title={t("common.internalReferenceHint")}>{referenceLabel(t, props.linkedPiNo) || "—"}</p> : <p>—</p>}
  </Frame>;
  return <PiPicker {...props}/>;
}
function PiPicker({linkedPiId,linkedPiNo,onPick,onClear,disabled,forBuild,bare}:PoPiLinkPanelProps) {
  const {t}=useTranslation();
  const [q,setQ]=useState("");
  // Ô chọn này là danh sách PI (màn bán hàng): đi theo quyền Xem PI và phạm vi khách của người dùng, nên giữ tên khách để chọn
  // đúng PI. Chọn xong, đơn mua chỉ lưu và hiện SỐ PI (YC-01, 07/10/2026 — khách bác lại được).
  const results=useProformaInvoices({q:q.trim() || undefined});
  return <Frame bare={bare}>
    <FieldLabel htmlFor="po-pi-search">{t("purchaseOrders.linkPi")}</FieldLabel>
    <p className="mb-3 text-xs text-ink-soft">{t(forBuild ? "poWorkspace.buildHint" : "poWorkspace.linkOnlyHint")}</p>
    {linkedPiId && <div className="mb-2 flex items-center gap-3"><span className="font-mono text-sm text-brand">{referenceLabel(t, linkedPiNo)}</span><Button size="sm" variant="ghost" disabled={disabled} onClick={onClear}>{t("purchaseOrders.clearPi")}</Button></div>}
    <Input id="po-pi-search" leading={<Search/>} placeholder={t("purchaseOrders.searchPi")} value={q} disabled={disabled} onChange={e=>setQ(e.target.value)} className="max-w-xl"/>
    <div className="mt-2 max-h-56 overflow-auto rounded-sm border border-line">
      {results.isLoading ? <p className="p-3 text-sm">{t("common.loading")}</p> : results.error ? <Button variant="secondary" onClick={()=>void results.refetch()}>{t("common.retry")}</Button> : !results.data?.data.length ? <p className="p-3 text-sm">{t("common.noMatches")}</p> : results.data.data.map(pi=><button key={pi.id} type="button" disabled={disabled} aria-pressed={pi.id===linkedPiId} onClick={()=>onPick(pi)} className={`grid w-full min-w-[30rem] grid-cols-[5rem_minmax(12rem,1fr)_12rem_3rem] gap-3 border-b border-line px-3 py-2 text-left text-xs hover:bg-brand-tint disabled:opacity-50 ${pi.id===linkedPiId ? "bg-brand-tint" : ""}`}>
        <span className="font-mono text-brand">{pi.piNo}</span><span>{pi.billToName}</span><span className="truncate font-mono" title={pi.customerPoNo ?? ""}>{pi.customerPoNo ?? "—"}</span><span className="text-right font-mono">{pi.itemCount}</span>
      </button>)}
    </div>
  </Frame>;
}
